import "./imageUploader.scss";
import { validateImageFile } from "./validation";
import * as EXIF from 'exif-js';
import * as Croppie from 'croppie';
import 'croppie/croppie.css';
import { ComponentRegistry } from '@ts/utils/ComponentRegistry';
import { icon } from "@fortawesome/fontawesome-svg-core";
import { faRotateLeft, faRotateRight } from "@fortawesome/free-solid-svg-icons";
import { Modal } from 'bootstrap';
import { AnimatedButton } from "../AnimatedButton";

// Croppie expects exif-js to be available globally.
const exifGlobal = (EXIF as any).default ?? EXIF;
(window as any).EXIF = exifGlobal;

type ImageUploaderHook = (data?: any) => void;

type ImageUploaderOptions = {
  cropperMode?: 'modal' | 'inline' | 'none';
  containerSelector?: string;
  inputFileName?: string;
  exportFileWidth?: number;
  exportFileHeight?: number;
  fileSizeLimit?: number;
  cropLabel?: string;
  cancelLabel?: string;
  quality?: number;
  backgroundColor?: string;
  uploadUrl?: string;
  onCancel?: ImageUploaderHook;
  onClick?: ImageUploaderHook;
  onError?: (error: string) => void;
  onSave?: (data: { success: boolean; viewUrl: string; file?: File }) => void;
  onSelect?: (imageUrl: string) => void;
  croppieOptions?: Croppie.CroppieOptions;
};

type CroppieExportData = {
  type: 'blob';
  size: 'original' | 'viewport';
  format: 'gif' | 'png' | 'jpeg' | 'webp';
  backgroundColor?: string;
  quality?: number;
};

export class ImageUploader {
  private readonly id: string;
  private readonly cropperMode: 'modal' | 'inline' | 'none';
  private readonly options: Required<Omit<ImageUploaderOptions, 'containerSelector'>> & Pick<ImageUploaderOptions, 'containerSelector'>;
  private static registry = new ComponentRegistry<ImageUploader>('image-uploader');

  private trigger: HTMLButtonElement | HTMLAnchorElement;
  private input: HTMLInputElement | null = null;
  private interfaceElement: HTMLElement | null = null;
  private saveBtnElement: HTMLButtonElement | null = null;
  private saveButton: AnimatedButton | null = null;
  private croppie: Croppie | null = null;
  private currentFileReader: FileReader | null = null;
  private allowedImageTypes: string[] = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
  private originalImageFormat: 'gif' |'png' | 'jpeg' | 'webp' = 'png';
  private modal: Modal | null = null;
  private previousFiles: FileList | null = null;

  private constructor(
    trigger: HTMLButtonElement | HTMLAnchorElement,
    options: ImageUploaderOptions
  ) {
    if (!(trigger instanceof HTMLButtonElement) && !(trigger instanceof HTMLAnchorElement)) {
      throw new Error('ImageUploader: Element must be an HTMLButtonElement or HTMLAnchorElement');
    }

    this.trigger = trigger;
    this.id = ImageUploader.registry.register(trigger, this);
    this.cropperMode = options.cropperMode ?? 'modal';

    if (this.cropperMode === 'inline' && !options.containerSelector) {
      throw new Error('ImageUploader: containerSelector is required when cropperMode is "inline"');
    }

    const defaultCroppieOptions: Croppie.CroppieOptions = {
      enableOrientation: true,
      enableExif: true,
      enforceBoundary: true,
      viewport: { width: 200, height: 200, type: 'square' },
      boundary: { width: 300, height: 300 }
    };

    this.options = {
      cropperMode: this.cropperMode,
      containerSelector: options.containerSelector,
      inputFileName: options.inputFileName ?? 'File',
      exportFileWidth: options.exportFileWidth ?? 500,
      exportFileHeight: options.exportFileHeight ?? 500,
      fileSizeLimit: options.fileSizeLimit ?? 5 * 1024 * 1024,
      uploadUrl: options.uploadUrl ?? '',
      cropLabel: options.cropLabel ?? 'Save Crop',
      cancelLabel: options.cancelLabel ?? 'Cancel',
      quality: options.quality ?? 0.8,
      backgroundColor: options.backgroundColor ?? '#ffffff',
      onCancel: options.onCancel ?? (() => {}),
      onClick: options.onClick ?? (() => {}),
      onError: options.onError ?? (() => {}),
      onSave: options.onSave ?? (() => {}),
      onSelect: options.onSelect ?? (() => {}),
      croppieOptions: {
        ...defaultCroppieOptions,
        ...options.croppieOptions
      }
    };
    this.init();
  }

  private init = (): void => {
    this.createWrapper();
    this.createFileInput();
    this.createInterface();
    this.bindTriggerEvents();
  };

  private createWrapper = (): void => {
    const wrapper = document.createElement('div');
    wrapper.className = 'image-uploader-wrapper';
    wrapper.id = this.id;
    
    this.trigger.parentNode?.insertBefore(wrapper, this.trigger);
    wrapper.appendChild(this.trigger);
  };

  private createFileInput = (): void => {
    const wrapper = document.getElementById(this.id);
    if (!wrapper) return;
    const existingFileInput = wrapper.querySelector<HTMLInputElement>(`input[type="file"][name="${this.options.inputFileName}"]`);
    if (existingFileInput) {
      this.input = existingFileInput;
      return;
    }

    // Create new input if none exists
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = this.allowedImageTypes.join(',');
    input.id = `${this.id}-input`;
    input.className = 'hidden';
    input.name = this.options.inputFileName;
    
    wrapper.appendChild(input);
    this.input = input;
  };

  private createInterface = (): void => {
    if (this.cropperMode === 'modal') {
      this.createModalInterface();
    } else if (this.cropperMode === 'inline') {
      this.createInlineInterface();
    }
  };

  private createModalInterface = (): void => {
    const modalId = `${this.id}-interface`;
    const modalElement = document.createElement('div');
    modalElement.className = 'modal fade';
    modalElement.setAttribute('tabindex', '-1');
    modalElement.setAttribute('role', 'dialog');
    modalElement.id = modalId;
    
    modalElement.innerHTML = `
      <div class="modal-crop-image modal-dialog modal-md" role="document">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">Crop Image</h5>
             <button type="button" class="modal-close" data-bs-dismiss="modal" aria-label="Close">
                  <i class="fa-solid fa-xmark"></i>
              </button>
          </div>
          <div class="modal-body">
            <div class="image-editor">
              <div class="cropped-preview"></div>
              <div class="image-tools">
                <button id="crop-save-btn" class="btn commit-crop-image" type="button">${this.options.cropLabel}</button>
                <button id="crop-cancel-btn" class="btn secondary cancel-crop-image" type="button">${this.options.cancelLabel}</button>
              </div>
            </div>
          </div>
        </div>
      </div>
    `;
    
    document.body.appendChild(modalElement);
    this.interfaceElement = modalElement;
    this.saveBtnElement = modalElement.querySelector('.commit-crop-image');
    this.initializeSaveButton();
    this.bindInterfaceEvents();
  };

  private createInlineInterface = (): void => {
    const inlineId = `${this.id}-interface`;
    const inlineElement = document.createElement('div');
    inlineElement.className = 'hidden';
    inlineElement.id = inlineId;
    inlineElement.innerHTML = `
      <div class="interface-content">
        <div class="image-editor">
          <div class="cropped-preview"></div>
          <div class="image-tools">
            <button id="crop-save-btn" class="btn btn-primary commit-crop-image" type="button">${this.options.cropLabel}</button>
            <button id="crop-cancel-btn" class="btn btn-secondary cancel-crop-image" type="button">${this.options.cancelLabel}</button>
          </div>
        </div>
      </div>
    `;
    
    const container = document.querySelector(this.options.containerSelector!);
    if (!container) {
      throw new Error(`ImageUploader: Container "${this.options.containerSelector}" not found`);
    }
    
    container.appendChild(inlineElement);
    this.interfaceElement = inlineElement;
    this.saveBtnElement = inlineElement.querySelector('.commit-crop-image');
    
    this.bindInterfaceEvents();
  };

  private bindTriggerEvents = (): void => {
    this.trigger.addEventListener('click', this.handleTriggerClick);
    this.input?.addEventListener('change', this.handleFileChange);
  };

  private initializeSaveButton = (): void => {
    if (!this.saveBtnElement) return;

    const buttons = AnimatedButton.create(this.saveBtnElement, {
      onClick: async (e, btn) => {
        e.preventDefault();
        
        if (!this.croppie) return;

        btn.setState('saving');

        try {
          const exportData = this.getExportData();
          const blob = await this.croppie.result(exportData) as Blob;

          const filename = this.buildCroppedFilename();
          const mimeType = blob.type || `image/${this.originalImageFormat}`;
          const file = new File([blob], filename, { type: mimeType });

          this.setInputFile(file);
          const viewUrl = this.createViewUrl(file);
          
          btn.setState('saved', 500, () => {
            if (this.cropperMode === 'modal') {
              this.modal?.hide();
            } else {
              this.interfaceElement?.classList.add('hidden');
            }

            btn.setState('default');
          });

          this.enableTrigger();
          this.options.onSave({ success: true, viewUrl, file });
        } catch (error) {
          console.error('Upload failed:', error);
          this.options.onError(error instanceof Error ? error.message : 'Upload failed');
          
          btn.setState('default');
          
          if (this.cropperMode === 'modal') {
            this.modal?.hide();
          }
        }
        return false;
      },
      savingLabel: 'Uploading...',
      savedLabel: 'Saved!'
    });

    this.saveButton = buttons?.[0] ?? null;
  };
  
  private bindInterfaceEvents = (): void => {
    if (!this.interfaceElement) return;
    
    const cancelBtns = this.interfaceElement.querySelectorAll('.cancel-crop-image');
    cancelBtns.forEach(btn => {
      btn.addEventListener('click', this.handleCancel);
    });
  };

  private handleTriggerClick = (e: Event): void => {
    e.preventDefault();
    if (this.trigger.classList.contains('disabled')) return;
    this.trigger.blur();
    this.storePreviousState();
    if (this.input) {
      this.input.value = '';
    }
    this.input?.click();
    this.options.onClick();
  };

  private postImage = async <T = any>(file: File): Promise<T> => {
    try {
      const form = new FormData();
      form.append(this.options.inputFileName, file, file.name);
      const res = await fetch(this.options.uploadUrl, {
        method: 'POST',
        body: form
      });

      if (!res.ok) {
        const message = `Upload failed (${res.status} ${res.statusText})`;
        this.options.onError(message);
        throw new Error(message);
      }

      return (await res.json()) as T;
    } catch (error) {
      console.error('Upload failed:', error);
      this.options.onError(error instanceof Error ? error.message : 'Upload failed');
      throw error;
    }
  };

  private handleFileChange = async (e: Event): Promise<void> => {
    if (this.currentFileReader) {
      this.currentFileReader.abort();
    }
    
    const input = e.target as HTMLInputElement;
    const file = input.files?.[0];

    if (!file) return;
    
    const validation = validateImageFile(file, this.options.fileSizeLimit, this.allowedImageTypes);
    if (!validation.valid) {
      this.options.onError(validation.message);
      return;
    }

    const mime = file.type;
    if (mime === 'image/jpeg') this.originalImageFormat = 'jpeg';
    else if (mime === 'image/png') this.originalImageFormat = 'png';
    else if (mime === 'image/webp') this.originalImageFormat = 'webp';
    
    if (this.cropperMode === 'none') {
      this.disableTrigger();

      try {
         const result = await this.postImage(file);
        const viewUrl = result.url;
        this.options.onSave(
          { success: true, viewUrl, file }
        );
        
      } catch (error) {
        console.error('Upload failed:', error);
        this.options.onError(error instanceof Error ? error.message : 'Upload failed');
      } finally {
        this.enableTrigger();
      }

      return;
    }

    this.currentFileReader = new FileReader();
    this.currentFileReader.onload = (event) => {
      const imageUrl = event.target?.result as string;
      this.options.onSelect(imageUrl);
      
      if (this.cropperMode === 'modal') {
        this.openModal(imageUrl);
      } else {
        this.displayInline(imageUrl);
      }
    };
    
    this.currentFileReader.readAsDataURL(file);
    input.value = '';
    this.disableTrigger();
  };

  private storePreviousState = (): void => {
    // Store previous file input state
    if (this.input?.files && this.input.files.length > 0) {
      const dt = new DataTransfer();
      Array.from(this.input.files).forEach(file => dt.items.add(file));
      this.previousFiles = dt.files;
    } else {
      this.previousFiles = null;
    }
  };
  
  private restorePreviousState = (): void => {
    // Restore file input
    if (this.input) {
      if (this.previousFiles && this.previousFiles.length > 0) {
        this.input.files = this.previousFiles;
      } else {
        // Clear the input if there was no previous file
        this.input.value = '';
      }
    }
    // Clear stored state
    this.previousFiles = null;
  };

  private handleCancel = (): void => {
    this.enableTrigger();
    
    if (this.cropperMode === 'modal') {
      this.modal?.hide();
    } else {
      this.interfaceElement?.classList.add('hidden');
    }
    
    // Restore previous state before calling onCancel
    this.restorePreviousState();
    
    this.options.onCancel();
  };

  private createCropperInstance = (container: HTMLElement, imageUrl: string): void => {
    this.destroyCroppie();
    
    const tempImg = new Image();
    tempImg.onload = () => {
      const viewport = this.options.croppieOptions.viewport;
      if (!viewport) return;
      
      const zoomForWidth = viewport.width / tempImg.width;
      const zoomForHeight = viewport.height / tempImg.height;
      const coverZoom = Math.max(zoomForWidth, zoomForHeight);
      const minZoom = Math.max(1, coverZoom);

      const instanceOptions: Croppie.CroppieOptions = {
        ...this.options.croppieOptions,
        minZoom
      };
      
      this.croppie = new Croppie(container, instanceOptions);
      this.croppie.bind({ url: imageUrl })
        .then(() => {
          this.addRotateControls();
        })
        .catch((error) => {
          console.error('Error binding image:', error);
          this.options.onError('Failed to load image for cropping');
        });
    };
    
    tempImg.onerror = () => {
      this.options.onError('Failed to load image');
    };
    
    tempImg.src = imageUrl;
  };

  private destroyCroppie = (): void => {
    if (this.croppie) {
      this.croppie.destroy();
      this.croppie = null;
    }
  };

  private addRotateControls = (): void => {
    const sliderWrap = this.interfaceElement?.querySelector('.cr-slider-wrap');
    if (!sliderWrap) return;
    
    const leftBtn = this.createRotateButton('left', 90);
    const rightBtn = this.createRotateButton('right', -90);
    
    sliderWrap.prepend(leftBtn);
    sliderWrap.appendChild(rightBtn);
    
    this.bindRotateEvents();
  };

  private createRotateButton = (direction: 'left' | 'right', degrees: number): HTMLButtonElement => {
    const button = document.createElement('button');
    button.className = `image-rotate rotate-${direction}`;
    button.setAttribute('data-deg', degrees.toString());
    button.setAttribute('type', 'button');
    
    const faIcon = direction === 'left' ? faRotateLeft : faRotateRight;
    button.innerHTML = icon(faIcon).html[0];
    
    return button;
  };

  private bindRotateEvents = (): void => {
    const rotateBtns = this.interfaceElement?.querySelectorAll('.image-rotate');
    rotateBtns?.forEach(btn => {
      btn.addEventListener('click', (e) => {
        if (!this.croppie) return;
        
        const degreeStr = (e.currentTarget as HTMLElement).dataset.deg ?? '0';
        const degree = parseInt(degreeStr, 10);
        this.croppie.rotate(degree);
      });
    });
  };

  private openModal = (imageUrl: string): void => {
    if (!this.interfaceElement) return;
    
    this.modal = Modal.getOrCreateInstance(this.interfaceElement);
    
    this.interfaceElement.addEventListener('shown.bs.modal', () => {
      const preview = this.interfaceElement?.querySelector('.cropped-preview') as HTMLElement;
      if (preview) {
        this.createCropperInstance(preview, imageUrl);
      }
    }, { once: true });
    
    this.interfaceElement.addEventListener('hidden.bs.modal', () => {
      this.destroyCroppie();
      this.enableTrigger();
    }, { once: true });
    
    this.modal.show();
  };

  private displayInline = (imageUrl: string): void => {
    if (!this.interfaceElement) return;
    
    this.interfaceElement.classList.remove('hidden');
    const preview = this.interfaceElement.querySelector('.cropped-preview') as HTMLElement;
    
    if (preview) {
      this.createCropperInstance(preview, imageUrl);
    }
  };


  private setInputFile = (file: File): void => {
    if (!this.input) return;

    // Programmatically setting <input type="file"> requires using FileList via DataTransfer.
    const dt = new DataTransfer();
    dt.items.add(file);
    this.input.files = dt.files;
  };

  private createViewUrl = (file: File): string => {
    try {
      return URL.createObjectURL(file);
    } catch {
      return '';
    }
  };

  private buildCroppedFilename = (): string => {
    const ext = this.originalImageFormat;
    return `cropped-image.${ext}`;
  };

  private getExportData = (): CroppieExportData => {
    const exportData: CroppieExportData = {
      type: 'blob',
      size: 'original',
      format: this.originalImageFormat
    };

    if (this.originalImageFormat === 'jpeg') {
      exportData.backgroundColor = this.options.backgroundColor;
      exportData.quality = this.options.quality;
    }

    return exportData;
  };

  private enableTrigger = (): void => {
    this.trigger.classList.remove('disabled');
  };

  private disableTrigger = (): void => {
    this.trigger.classList.add('disabled');
  };

  public static create(
    target: string | HTMLButtonElement | HTMLAnchorElement | NodeListOf<HTMLElement> | HTMLElement[],
    options: ImageUploaderOptions = {}
  ): ImageUploader[] | null {
    const triggers = ComponentRegistry.processTargets<ImageUploader, HTMLElement>(
        ImageUploader.registry,
        target,
        [HTMLButtonElement, HTMLAnchorElement]
    ) as (HTMLButtonElement | HTMLAnchorElement)[];

    if (!triggers.length) return null;
    return triggers.map(trigger => new ImageUploader(trigger, options));
  }

  public static getUploader(id: string): ImageUploader | null {
      return ImageUploader.registry.get(id) ?? null;
  }

  public getId = (): string => this.id;

  public destroy = (): void => {
    this.destroyCroppie();
    this.saveButton?.destroy();
    this.trigger.removeEventListener('click', this.handleTriggerClick);
    this.input?.removeEventListener('change', this.handleFileChange);
    this.interfaceElement?.remove();
    this.modal?.dispose();

    ImageUploader.registry.unregister(this.id);
  };
}

export const setImageSrc = (selector: string, imageUrl: string, showLoading = false): void => {
  if (!imageUrl) return;

  try {
    const img = document.querySelector<HTMLImageElement>(selector);
    if (!img) return;

    const rand = Math.floor(1000 + Math.random() * 9000);
    const hasParams = imageUrl.includes('?');
    const versionedUrl = hasParams ? `${imageUrl}&v=${rand}` : `${imageUrl}?v=${rand}`;

    img.classList.toggle('loading', showLoading);
    img.src = versionedUrl;
    
    if (!showLoading) {
      img.dataset.savedImage = imageUrl;
    }
  } catch (error) {
    console.error('Failed to set image src:', error);
  }
};